import { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@/components/Modal";
import { buttonVariants } from "@markeroo/theme";
import { ui } from "@/strings";

interface DeleteButtonProps {
  onClick: () => void;
  confirmMessage?: string;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * DeleteButton provides a styled button for destructive delete actions.
 * Optionally shows a confirmation modal before executing the delete.
 * Automatically stops event propagation to prevent triggering parent click handlers.
 */
export function DeleteButton({
  onClick,
  confirmMessage,
  loading = false,
  disabled = false,
}: DeleteButtonProps) {
  const [isConfirmOpen, setConfirmOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmMessage) {
      setConfirmOpen(true);
    } else {
      onClick();
    }
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    onClick();
  };

  return (
    <>
      <button
        type="button"
        className={`${buttonVariants.ghostDanger} w-full sm:w-auto`}
        onClick={handleClick}
        disabled={loading || disabled}
      >
        {loading ? ui.actions.deleting : ui.actions.delete}
      </button>

      {confirmMessage && (
        <Modal isOpen={isConfirmOpen} onClose={() => setConfirmOpen(false)}>
          <ModalHeader
            title={`${ui.actions.confirm} ${ui.actions.delete}`}
            onClose={() => setConfirmOpen(false)}
          />
          <ModalBody>
            <p>{confirmMessage}</p>
          </ModalBody>
          <ModalFooter>
            <button
              className={buttonVariants.secondary}
              onClick={() => setConfirmOpen(false)}
            >
              {ui.actions.cancel}
            </button>
            <button className={buttonVariants.danger} onClick={handleConfirm}>
              {ui.actions.delete}
            </button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}
